import type { SedrRequirement, SedrRequirementLinkType } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import {
  canMutateRequirementLinks,
  statusBadgeClass,
  tSedrLinkTypeName,
  tSedrStatus,
} from './sedrUi';

export interface SedrAddRequirementLinkDialogOptions {
  source: SedrRequirement;
  candidates: SedrRequirement[];
  linkTypes: SedrRequirementLinkType[];
}

export interface SedrAddRequirementLinkResult {
  toRequirementId: string;
  linkType: string;
}

/**
 * Modal to pick a link type and target requirement.
 * Returns the selection, or null if cancelled.
 */
export async function showSedrAddRequirementLinkDialog(
  input: SedrAddRequirementLinkDialogOptions,
): Promise<SedrAddRequirementLinkResult | null> {
  const existingByType = new Map<string, Set<string>>();
  for (const link of input.source.links ?? []) {
    const set = existingByType.get(link.linkType) ?? new Set<string>();
    set.add(link.otherRequirementId);
    existingByType.set(link.linkType, set);
  }

  const eligible = input.candidates
    .filter((r) => r.id !== input.source.id && canMutateRequirementLinks(r.status))
    .sort((a, b) =>
      a.requirementCode.localeCompare(b.requirementCode, undefined, { sensitivity: 'base' }),
    );

  const types = [...input.linkTypes].sort((a, b) => {
    if (a.isBuiltIn && !b.isBuiltIn) return -1;
    if (!a.isBuiltIn && b.isBuiltIn) return 1;
    return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  });

  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'sedr.links.addTitle',
      className: 'sedr-add-link-dialog',
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="sedr.links.addHint"></p>
      <label class="sedr-add-link-dialog__type">
        <span data-i18n="sedr.links.type"></span>
        <select id="sedr-add-link-type"></select>
      </label>
      <div class="sedr-supersede-target">
        <div class="sedr-supersede-target__toolbar">
          <input
            type="search"
            class="files-explorer-bar__search sedr-supersede-target__search"
            id="sedr-add-link-search"
            data-i18n-placeholder="sedr.links.search"
          />
        </div>
        <div class="files-table-wrap sedr-supersede-target__table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th data-i18n="sedr.table.reqId"></th>
                <th data-i18n="sedr.table.title"></th>
                <th data-i18n="sedr.table.status"></th>
              </tr>
            </thead>
            <tbody id="sedr-add-link-tbody"></tbody>
          </table>
          <p class="muted sedr-supersede-target__empty" id="sedr-add-link-empty" hidden data-i18n="sedr.links.emptyCandidates"></p>
        </div>
        <div class="sedr-supersede-target__selection" id="sedr-add-link-selection" aria-live="polite"></div>
      </div>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'button primary';
    confirmButton.disabled = true;
    confirmButton.innerHTML = '<span data-i18n="sedr.links.addConfirm"></span>';

    shell.actions.append(cancelButton, confirmButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    const typeSelect = shell.body.querySelector('#sedr-add-link-type') as HTMLSelectElement;
    const table = shell.body.querySelector('table.data-table') as HTMLTableElement;
    const tbody = shell.body.querySelector('#sedr-add-link-tbody') as HTMLTableSectionElement;
    const emptyEl = shell.body.querySelector('#sedr-add-link-empty') as HTMLParagraphElement;
    const selectionEl = shell.body.querySelector('#sedr-add-link-selection') as HTMLElement;
    const searchInput = shell.body.querySelector('#sedr-add-link-search') as HTMLInputElement;
    const byId = new Map(eligible.map((r) => [r.id, r]));

    for (const type of types) {
      const opt = document.createElement('option');
      opt.value = type.id;
      opt.textContent = type.isBuiltIn
        ? tSedrLinkTypeName(type.id, type.name, false)
        : type.name;
      typeSelect.append(opt);
    }

    let selectedId: string | null = null;
    let searchQuery = '';

    const finish = (value: SedrAddRequirementLinkResult | null) => {
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    const currentType = () => typeSelect.value;

    const candidatesForType = () => {
      const linked = existingByType.get(currentType()) ?? new Set<string>();
      return eligible.filter((r) => !linked.has(r.id));
    };

    const updateSelectionChrome = () => {
      confirmButton.disabled = !selectedId || !currentType();
      selectionEl.replaceChildren();

      if (!selectedId) {
        selectionEl.className = 'sedr-supersede-target__selection sedr-supersede-target__selection--empty';
        const label = document.createElement('span');
        label.className = 'muted';
        label.textContent = t('sedr.links.noneSelected');
        selectionEl.append(label);
      } else {
        const req = byId.get(selectedId);
        selectionEl.className = 'sedr-supersede-target__selection';
        const caption = document.createElement('span');
        caption.className = 'sedr-supersede-target__selection-label';
        caption.textContent = t('sedr.links.selected');
        selectionEl.append(caption);
        const detail = document.createElement('span');
        detail.className = 'sedr-supersede-target__selection-detail';
        detail.textContent = req
          ? `${req.requirementCode} — ${req.title}`
          : selectedId;
        selectionEl.append(detail);
      }

      for (const row of tbody.querySelectorAll('.sedr-supersede-target__row')) {
        const id = row.getAttribute('data-requirement-id');
        row.classList.toggle('sedr-supersede-target__row--selected', id === selectedId);
      }
    };

    const refreshListing = () => {
      const query = searchQuery.trim().toLowerCase();
      const pool = candidatesForType();
      const visible = query
        ? pool.filter(
            (r) =>
              r.requirementCode.toLowerCase().includes(query)
              || r.title.toLowerCase().includes(query),
          )
        : pool;

      if (selectedId && !pool.some((r) => r.id === selectedId)) {
        selectedId = null;
      }

      tbody.replaceChildren();
      for (const req of visible) {
        const tr = document.createElement('tr');
        tr.className = 'sedr-supersede-target__row';
        tr.dataset.requirementId = req.id;
        tr.tabIndex = 0;

        const codeCell = document.createElement('td');
        const codeBtn = document.createElement('button');
        codeBtn.type = 'button';
        codeBtn.className = 'files-table__name-button';
        codeBtn.textContent = req.requirementCode;
        codeCell.append(codeBtn);
        tr.append(codeCell);

        const titleCell = document.createElement('td');
        titleCell.textContent = req.title;
        tr.append(titleCell);

        const statusCell = document.createElement('td');
        const badge = document.createElement('span');
        badge.className = statusBadgeClass(req.status);
        badge.textContent = tSedrStatus(req.status);
        statusCell.append(badge);
        tr.append(statusCell);

        const activate = () => {
          selectedId = req.id;
          updateSelectionChrome();
        };
        const confirmSelection = () => {
          selectedId = req.id;
          if (currentType()) {
            finish({ toRequirementId: req.id, linkType: currentType() });
          }
        };

        codeBtn.addEventListener('click', activate);
        codeBtn.addEventListener('dblclick', confirmSelection);
        tr.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          activate();
        });
        tr.addEventListener('dblclick', confirmSelection);
        tr.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmSelection();
          }
        });

        tbody.append(tr);
      }

      emptyEl.hidden = visible.length > 0;
      enhanceDataTableCards(table);
      applyI18n(shell.dialog);
      updateSelectionChrome();
    };

    typeSelect.addEventListener('change', () => refreshListing());
    searchInput.addEventListener('input', () => {
      searchQuery = searchInput.value;
      refreshListing();
    });
    cancelButton.addEventListener('click', () => finish(null));
    confirmButton.addEventListener('click', () => {
      if (selectedId && currentType()) {
        finish({ toRequirementId: selectedId, linkType: currentType() });
      }
    });

    refreshListing();
    typeSelect.focus();
  });
}
